import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  ShieldAlert, ShieldCheck, Loader2, RefreshCw, AlertTriangle, Eye,
  Check, BookmarkCheck, RotateCcw, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * Iter-194 — Production Data Integrity Card.
 *
 * Renders on the Admin Dashboard. Shows:
 *   • Summary badge: # of suspicious shifts auto-hidden + # open alerts
 *   • Open alerts list with severity tone (warning/critical), type, count
 *   • "Run scan now" button — triggers an on-demand sweep
 *   • Drill-into-shifts modal showing the offending rows + resolve flow
 *
 * Data sources:
 *   GET  /api/admin/data-integrity/summary
 *   GET  /api/admin/data-integrity/alerts?status=open
 *   POST /api/admin/data-integrity/scan
 *   GET  /api/admin/data-integrity/alerts/{id}/shifts
 *   POST /api/admin/data-integrity/alerts/{id}/resolve
 */
const ALERT_LABELS = {
  repeated_title: 'Repeated event title',
  empty_title_burst: 'Empty-title burst insert',
  keyword_hit: 'Test/seed keyword detected',
  burst_insert: 'Burst inserts by single admin',
  bulk_seed: 'Bulk seed pattern',
};

const SEVERITY_TONE = {
  info: 'bg-sky-50 border-sky-200 text-sky-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  critical: 'bg-rose-50 border-rose-200 text-rose-900',
};

const DataIntegrityCard = () => {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [drill, setDrill] = useState(null); // { alert, shifts, loading }
  const [actionNote, setActionNote] = useState('');
  const [actionInFlight, setActionInFlight] = useState(false);
  const [actionMode, setActionMode] = useState('resolve'); // resolve | acknowledge | expected | reopen
  const [testingNotify, setTestingNotify] = useState(false);

  const load = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get('/admin/data-integrity/summary'),
        api.get('/admin/data-integrity/alerts', { params: { status: 'open', limit: 25 } }),
      ]);
      setSummary(s.data);
      setAlerts(a.data?.alerts || []);
    } catch (e) {
      // Silently skip — endpoint is admin-only; non-admins won't render this card.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await api.post('/admin/data-integrity/scan', null, { params: { lookback_hours: 24, auto_hide: true } });
      const d = res.data || {};
      toast.success(
        `Scan complete — ${d.alerts_raised || 0} new alerts · ${d.auto_hidden_in_window || 0} rows auto-hidden in last 24h`,
        { duration: 5000 },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const openDrill = async (alert) => {
    setDrill({ alert, shifts: [], loading: true });
    setActionNote('');
    setActionMode('resolve');
    try {
      const res = await api.get(`/admin/data-integrity/alerts/${alert.id}/shifts`);
      setDrill({ alert, shifts: res.data?.shifts || [], loading: false });
    } catch (e) {
      toast.error('Could not load offending shifts');
      setDrill({ alert, shifts: [], loading: false });
    }
  };

  const closeDrill = () => { setDrill(null); setActionNote(''); };

  const runAction = async () => {
    if (!drill?.alert?.id) return;
    if ((actionNote || '').trim().length < 3) {
      toast.error('Add a short note (min 3 chars) describing the action');
      return;
    }
    const endpoints = {
      resolve:     'resolve',
      acknowledge: 'acknowledge',
      expected:    'mark-expected',
      reopen:      'reopen',
    };
    const verb = {
      resolve: 'Resolved', acknowledge: 'Acknowledged',
      expected: 'Marked as expected', reopen: 'Re-opened',
    }[actionMode] || 'Updated';
    setActionInFlight(true);
    try {
      await api.post(
        `/admin/data-integrity/alerts/${drill.alert.id}/${endpoints[actionMode]}`,
        { note: actionNote.trim() },
      );
      toast.success(`${verb}`);
      closeDrill();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    } finally {
      setActionInFlight(false);
    }
  };

  const sendTestNotification = async () => {
    setTestingNotify(true);
    try {
      const res = await api.post('/admin/data-integrity/notifications/test');
      const d = res.data || {};
      if ((d.sent_to || []).length > 0) {
        toast.success(`Test email sent to ${d.sent_to.join(', ')}`);
      } else if (d.skipped_cooldown) {
        toast.info('Already sent recently — skipped by 6h cooldown');
      } else {
        toast.warning(`No recipients matched. Errors: ${(d.errors || []).join('; ') || '(none)'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setTestingNotify(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="data-integrity-card-loading">
        <CardHeader><CardTitle className="text-sm">Production Data Integrity</CardTitle></CardHeader>
        <CardContent><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></CardContent>
      </Card>
    );
  }

  // Empty / healthy state — green pill, still surfaces the "Run scan" CTA.
  const isHealthy = (summary?.open_alerts || 0) === 0 && (summary?.auto_hidden_rows || 0) === 0;
  const hasCritical = (summary?.open_critical || 0) > 0;

  return (
    <>
      <Card data-testid="data-integrity-card" className={hasCritical ? 'border-rose-300' : isHealthy ? 'border-emerald-200' : 'border-amber-200'}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            {hasCritical ? (
              <ShieldAlert className="w-4 h-4 text-rose-600" />
            ) : isHealthy ? (
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            )}
            Production Data Integrity
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={runScan}
            disabled={scanning}
            data-testid="data-integrity-scan-btn"
          >
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {scanning ? 'Scanning…' : 'Run scan'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Iter-195 — test notification probe so admins can verify the
              recipient list + cooldown wiring without waiting for a
              real critical alert. */}
          <div className="flex items-center justify-between text-[11px] text-gray-500">
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" /> Critical alerts email super-admins immediately · warnings digest daily (09:00 UTC)
            </span>
            <button
              type="button"
              onClick={sendTestNotification}
              disabled={testingNotify}
              className="underline hover:text-cyan-600 disabled:opacity-50"
              data-testid="data-integrity-send-test-btn"
            >
              {testingNotify ? 'Sending…' : 'Send test alert'}
            </button>
          </div>
          {/* Summary chips */}
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline" data-testid="data-integrity-open-count" className={hasCritical ? 'border-rose-300 text-rose-700' : ''}>
              {summary?.open_alerts || 0} open alert{summary?.open_alerts === 1 ? '' : 's'}
            </Badge>
            <Badge variant="outline" data-testid="data-integrity-hidden-count" className="border-amber-300 text-amber-800">
              {summary?.auto_hidden_rows || 0} suspicious shifts auto-hidden
            </Badge>
            {hasCritical && (
              <Badge className="bg-rose-100 text-rose-800 border border-rose-300" data-testid="data-integrity-critical-badge">
                {summary.open_critical} critical
              </Badge>
            )}
            {isHealthy && (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid="data-integrity-healthy-badge">
                Test records excluded from production surfaces
              </Badge>
            )}
          </div>

          {/* Alert list */}
          {alerts.length === 0 ? (
            <p className="text-xs text-gray-500" data-testid="data-integrity-empty">
              No open data-integrity alerts. The scanner sweeps the last 24h every hour.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="data-integrity-alert-list">
              {alerts.slice(0, 6).map((a) => (
                <li
                  key={a.id}
                  className={`border rounded-md px-2.5 py-2 flex items-start justify-between gap-2 text-xs ${SEVERITY_TONE[a.severity] || SEVERITY_TONE.warning}`}
                  data-testid={`data-integrity-alert-${a.alert_type}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold flex items-center gap-1.5">
                      <span>{ALERT_LABELS[a.alert_type] || a.alert_type}</span>
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4 leading-none">
                        {a.severity}
                      </Badge>
                      {a.auto_hidden && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4 leading-none border-emerald-300 text-emerald-700">
                          auto-hidden
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] opacity-80 truncate">
                      {a.suspicious_count} record{a.suspicious_count === 1 ? '' : 's'}
                      {a.details?.title_normalized ? ` · "${a.details.title_normalized}"` : ''}
                      {a.details?.city ? ` · ${a.details.city}${a.details.state ? `, ${a.details.state}` : ''}` : ''}
                      {a.details?.keyword ? ` · keyword: ${a.details.keyword}` : ''}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => openDrill(a)}
                    data-testid={`data-integrity-alert-review-${a.id}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> Review
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {alerts.length > 6 && (
            <p className="text-[11px] text-gray-500" data-testid="data-integrity-more">
              + {alerts.length - 6} more alert{alerts.length - 6 === 1 ? '' : 's'} hidden — open the dashboard's data-integrity drawer for the full list.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Drill-down + resolve modal */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) closeDrill(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="data-integrity-drill-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              {drill?.alert ? ALERT_LABELS[drill.alert.alert_type] || drill.alert.alert_type : 'Alert'}
            </DialogTitle>
            <DialogDescription>
              {drill?.alert?.suspicious_count || 0} suspicious shift{drill?.alert?.suspicious_count === 1 ? '' : 's'}
              {drill?.alert?.auto_hidden ? ' — auto-hidden from production surfaces (is_test=TRUE).' : ' — flagged for review (not auto-hidden).'}
            </DialogDescription>
          </DialogHeader>
          {drill?.loading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading offending shifts…</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto border rounded p-2 bg-gray-50" data-testid="data-integrity-drill-list">
              {(drill?.shifts || []).length === 0 ? (
                <p className="text-xs text-gray-500">No shifts found for this alert.</p>
              ) : (
                drill.shifts.map((s) => (
                  <div key={s.id} className="text-[11px] border bg-white rounded px-2 py-1.5 flex items-center justify-between gap-2" data-testid={`data-integrity-drill-shift-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{s.event_title || '(no title)'} · {s.role_name || 'No role'}</div>
                      <div className="text-gray-500 truncate">{s.venue_name || '—'} · {s.city || '?'}, {s.state || '?'} · {s.shift_date || '—'}</div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      {s.is_test && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">hidden</Badge>}
                      {s.is_archived && <Badge className="bg-gray-100 text-gray-700 text-[10px]">archived</Badge>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="space-y-1 border-t pt-3">
            {/* Iter-195 — action picker: resolve / acknowledge / expected / reopen */}
            <label className="text-xs font-semibold text-gray-700">Action</label>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="data-integrity-action-picker">
              {[
                { key: 'resolve',     label: 'Resolve',          icon: Check,         tone: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
                { key: 'acknowledge', label: 'Acknowledge',      icon: BookmarkCheck, tone: 'bg-sky-50 text-sky-800 border-sky-300' },
                { key: 'expected',    label: 'Mark expected',    icon: ShieldCheck,   tone: 'bg-amber-50 text-amber-800 border-amber-300' },
                { key: 'reopen',      label: 'Re-open',          icon: RotateCcw,     tone: 'bg-rose-50 text-rose-800 border-rose-300' },
              ].map(({ key, label, icon: Icon, tone }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActionMode(key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                    actionMode === key ? tone + ' ring-2 ring-offset-1 ring-cyan-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`data-integrity-action-${key}`}
                >
                  <Icon className="w-3 h-3" />
                  {label}
                </button>
              ))}
            </div>
            <label className="text-xs font-semibold text-gray-700">Note (required, min 3 chars)</label>
            <Textarea
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={
                actionMode === 'resolve'     ? 'e.g. Confirmed legitimate CSV import from acme-co.' :
                actionMode === 'acknowledge' ? 'e.g. Investigating with imports team — assigned to me.' :
                actionMode === 'expected'    ? 'e.g. Real venue contains the word "Test" — confirmed safe.' :
                                               'e.g. Resurfaced after follow-up — re-opening for review.'
              }
              data-testid="data-integrity-action-note"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDrill} disabled={actionInFlight}>Close</Button>
            <Button
              onClick={runAction}
              disabled={actionInFlight || (actionNote || '').trim().length < 3}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="data-integrity-action-submit"
            >
              {actionInFlight ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {actionMode === 'resolve' ? 'Mark resolved' :
               actionMode === 'acknowledge' ? 'Acknowledge' :
               actionMode === 'expected' ? 'Mark expected' : 'Re-open alert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DataIntegrityCard;
